import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { PlusIcon, Calendar, Clock, CheckCircleIcon, Users, Video, ExternalLink, XIcon, EditIcon, Trash2Icon, ChevronRight } from 'lucide-react';
import { coffeeData, Coffee } from '../components/coffee/coffeeData';
import CreateCoffeeModal from '../components/coffee/CreateCoffeeModal';
import { useNavigate, Link } from 'react-router-dom';
// Move formatting functions outside components to make them accessible
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
};
const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit'
  });
};
interface EventCardProps {
  event: Coffee;
  onClick: () => void;
  onEdit?: (coffee: Coffee) => void;
  onDelete?: (coffee: Coffee) => void;
}
const EventCard: React.FC<EventCardProps> = ({
  event,
  onClick,
  onEdit,
  onDelete
}) => {
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const now = new Date();
  const coffeeDate = new Date(event.date);
  const isPast = coffeeDate < now;
  const isJoined = event.participants.some(p => p.id === 'current-user');
  const hasParticipated = isPast && isJoined;
  const isHost = event.host.id === 'current-user';
  const hasPendingRequests = event.pendingRequests && event.pendingRequests.length > 0;
  // Platform logos
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'Google Meet':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/hawwkzWf3TnSE8Q31Hrx5c/images.png';
      case 'Microsoft Teams':
        return 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg';
      case 'Zoom':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a6M2ckhEkmcVgkXUVkifgg/zoom_logo_icon_147196.png';
      default:
        return null;
    }
  };
  const handleCardClick = (e: React.MouseEvent) => {
    // Only trigger the main card click if we're not clicking on the menu or its buttons
    if (!(e.target as HTMLElement).closest('.coffee-card-menu') && !(e.target as HTMLElement).closest('.coffee-card-action-btn')) {
      onClick();
    }
  };
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(event);
    }
  };
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
        onDelete(event);
      }
    }
  };
  const handleAcceptRequest = (requestId: string) => {
    console.log(`Accepted request ${requestId}`);
    // In a real app, you would make an API call here
    // For now, just close the modal
    setShowRequestsModal(false);
  };
  const handleRejectRequest = (requestId: string) => {
    console.log(`Rejected request ${requestId}`);
    // In a real app, you would make an API call here
    // For now, just close the modal
    setShowRequestsModal(false);
  };
  return <div className="bg-white rounded-xl border border-gray-200 
      overflow-hidden h-full flex flex-col hover:shadow-md transition-shadow cursor-pointer relative" onClick={handleCardClick} data-id="element-2758">
      <div className="p-5 flex-1 flex flex-col" data-id="element-2759">
        <div className="flex items-start mb-4" data-id="element-2760">
          <div className="w-12 h-12 rounded-full overflow-hidden mr-3 flex-shrink-0 border-2 border-custom-green" data-id="element-2761">
            <img src={event.host.avatar} alt={event.host.name} className="w-full h-full object-cover" data-id="element-2762" />
          </div>
          <div className="flex-1" data-id="element-2763">
            <h3 className="text-base font-semibold text-gray-800 mb-1" data-id="element-2764">
              {event.title}
            </h3>
            <p className="text-sm text-gray-500 mb-2" data-id="element-2765">Organizzato da te</p>
            {/* Show pending requests badge next to the title */}
            {hasPendingRequests && !isPast && <div className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium" data-id="element-2766">
                <Users size={12} className="mr-1" data-id="element-2767" />
                {event.pendingRequests.length} richieste in attesa
              </div>}
          </div>
          {isHost && !isPast && <div className="coffee-card-menu ml-2" data-id="element-2768">
              <div className="flex space-x-1" data-id="element-2769">
                <button onClick={handleEditClick} className="p-1 rounded-full hover:bg-gray-100" title="Modifica evento" data-id="element-2770">
                  <EditIcon size={14} className="text-gray-500" data-id="element-2771" />
                </button>
                <button onClick={handleDeleteClick} className="p-1 rounded-full hover:bg-gray-100" title="Elimina evento" data-id="element-2772">
                  <Trash2Icon size={14} className="text-gray-500" data-id="element-2773" />
                </button>
              </div>
            </div>}
        </div>

        <div className="flex flex-wrap gap-2 mb-4" data-id="element-2774">
          {event.topics.map((topic, index) => <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs" data-id="element-2775">
              {topic}
            </span>)}
          {hasParticipated && <span className="px-2 py-1 bg-custom-green/10 text-custom-green rounded-full text-xs flex items-center" data-id="element-2776">
              <CheckCircleIcon size={12} className="mr-1" data-id="element-2777" />
              Hai partecipato
            </span>}
          {isHost && !isPast && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center" data-id="element-2778">
              Sei l'host
            </span>}
        </div>

        <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-2779">
          <Calendar size={16} className="mr-2" data-id="element-2780" />
          {formatDate(event.date)}
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-2781">
          <Clock size={16} className="mr-2" data-id="element-2782" />
          {formatTime(event.date)} - {formatTime(event.endTime)}
        </div>
        <div className="flex items-center text-sm text-gray-500 mb-4" data-id="element-2783">
          {getPlatformLogo(event.platform) ? <img src={getPlatformLogo(event.platform)} alt={event.platform} className="w-5 h-5 mr-2 object-contain" data-id="element-2784" /> : <Video size={16} className="mr-2" data-id="element-2785" />}
          {event.platform}
        </div>

        <p className="text-sm text-gray-600 mb-4" data-id="element-2786">{event.description}</p>

        {/* Participants and pending requests section */}
        <div className="space-y-3 mb-4" data-id="element-2787">
          <div className="flex items-center" data-id="element-2788">
            <Users size={16} className="text-gray-500 mr-2" data-id="element-2789" />
            <span className="text-sm text-gray-500" data-id="element-2790">
              {event.participants.length === 0 ? 'Nessun partecipante' : `${event.participants.length} partecipant${event.participants.length === 1 ? 'e' : 'i'}`}
            </span>
          </div>

          {/* Pending requests section - more prominent */}
          {hasPendingRequests && !isPast && <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-3" data-id="element-2791">
              <div className="flex items-center justify-between" data-id="element-2792">
                <div className="flex items-center" data-id="element-2793">
                  <Users size={16} className="text-yellow-600 mr-2" data-id="element-2794" />
                  <span className="text-sm font-medium text-yellow-700" data-id="element-2795">
                    {event.pendingRequests.length} richieste di partecipazione
                    in attesa
                  </span>
                </div>
                <button onClick={e => {
              e.stopPropagation();
              setShowRequestsModal(true);
            }} className="coffee-card-action-btn text-xs px-3 py-1 bg-white text-yellow-700 border border-yellow-300 rounded-md hover:bg-yellow-100 transition-colors" data-id="element-2796">
                  Gestisci
                </button>
              </div>
              {/* Preview of pending requests */}
              <div className="mt-2 flex -space-x-2 overflow-hidden" data-id="element-2797">
                {event.pendingRequests.slice(0, 3).map(request => <div key={request.id} className="inline-block h-6 w-6 rounded-full ring-2 ring-white" data-id="element-2798">
                    <img src={request.avatar} alt={request.name} className="h-full w-full object-cover rounded-full" data-id="element-2799" />
                  </div>)}
                {event.pendingRequests.length > 3 && <div className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-200 text-xs font-medium text-gray-800 ring-2 ring-white" data-id="element-2800">
                    +{event.pendingRequests.length - 3}
                  </div>}
              </div>
            </div>}
        </div>

        <div className="mt-auto flex flex-col gap-2" data-id="element-2801">
          {isPast ? hasParticipated ? <button className="w-full flex items-center justify-center py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-2802">
                Dettagli
              </button> : <button disabled className="w-full py-2 bg-gray-200 text-gray-500 rounded-lg font-medium" data-id="element-2803">
                Terminato
              </button> : <>
              <button className="w-full flex items-center justify-center py-2 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors" data-id="element-2804">
                <ExternalLink size={16} className="mr-2" data-id="element-2805" />
                Partecipa
              </button>
              {isHost && <div className="flex gap-2" data-id="element-2806">
                  <button onClick={handleEditClick} className="coffee-card-action-btn flex-1 py-2 px-4 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-2807">
                    Modifica
                  </button>
                  <button onClick={handleDeleteClick} className="coffee-card-action-btn flex-1 py-2 px-4 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm" data-id="element-2808">
                    Elimina
                  </button>
                </div>}
            </>}
        </div>
      </div>

      {/* Pending Requests Modal */}
      {showRequestsModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={e => {
      e.stopPropagation();
      setShowRequestsModal(false);
    }} data-id="element-2809">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-2810">
            <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2811">
              Richieste di partecipazione
            </h3>
            <div className="space-y-3" data-id="element-2812">
              {event.pendingRequests && event.pendingRequests.map(request => <div key={request.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3" data-id="element-2813">
                    <div className="flex items-center" data-id="element-2814">
                      <div className="w-10 h-10 rounded-full overflow-hidden mr-3" data-id="element-2815">
                        <img src={request.avatar} alt={request.name} className="w-full h-full object-cover" data-id="element-2816" />
                      </div>
                      <div data-id="element-2817">
                        <p className="font-medium text-gray-800" data-id="element-2818">
                          {request.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-2819">
                      <button onClick={() => handleRejectRequest(request.id)} className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full" data-id="element-2820">
                        <XIcon size={16} data-id="element-2821" />
                      </button>
                      <button onClick={() => handleAcceptRequest(request.id)} className="p-2 text-gray-500 hover:text-custom-green hover:bg-custom-green/10 rounded-full" data-id="element-2822">
                        <CheckCircleIcon size={16} data-id="element-2823" />
                      </button>
                    </div>
                  </div>)}
            </div>
            <div className="mt-6 flex justify-end" data-id="element-2824">
              <button onClick={() => setShowRequestsModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-2825">
                Chiudi
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
const MyCoffeePage = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [coffeeToEdit, setCoffeeToEdit] = useState<Coffee | null>(null);
  const [localCoffeeData, setLocalCoffeeData] = useState([...coffeeData]);
  const navigate = useNavigate();
  // Helper function to determine if an event is past and user participated
  const isPastAndParticipated = (event: Coffee) => {
    const now = new Date();
    const eventDate = new Date(event.date);
    const isPast = eventDate < now;
    const isParticipant = event.participants.some(p => p.id === 'current-user');
    return isPast && isParticipant;
  };
  // Filtrare gli eventi creati dall'utente corrente
  const myHostedEvents = localCoffeeData.filter(coffee => coffee.host.id === 'current-user' || coffee.host.id.startsWith('admin') && coffee.participants.some(p => p.id === 'current-user'));
  const handleCoffeeClick = (coffeeId: string) => {
    navigate(`/coffee/${coffeeId}`);
  };
  const handleEditCoffee = (coffee: Coffee) => {
    setCoffeeToEdit(coffee);
  };
  const handleDeleteCoffee = (coffee: Coffee) => {
    // In a real app, this would make an API call
    // For now, we'll just update the local state
    const updatedData = localCoffeeData.filter(c => c.id !== coffee.id);
    setLocalCoffeeData(updatedData);
    // Show a success message
    alert('Evento eliminato con successo!');
  };
  return <div className="flex h-screen bg-gray-100" data-id="element-2826">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`} data-id="element-2827">
        {/* Sidebar backdrop */}
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-2828"></div>
        {/* Sidebar */}
        <div className="relative flex-1 flex flex-col w-2/3 h-full bg-black" data-id="element-2829">
          <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-2830">
            <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-2831">
              <span className="sr-only" data-id="element-2832">Close sidebar</span>
              <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-2833" />
            </button>
          </div>
          <div className="flex-1 h-full overflow-y-auto" data-id="element-2834">
            <Sidebar activePage="community" data-id="element-2835" />
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0" data-id="element-2836">
        <Sidebar activePage="community" data-id="element-2837" />
      </div>

      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-2838">
        <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-2839" />

        <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-0 md:pt-0 bg-gray-100" data-id="element-2840">
          <div className="pt-4 md:pt-6 max-w-6xl mx-auto" data-id="element-2841">
            {/* Breadcrumbs */}
            <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-2842">
              <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-2843">
                <li className="inline-flex items-center" data-id="element-2844">
                  <Link to="/coffee" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900" data-id="element-2845">
                    Eventi Greenback
                  </Link>
                </li>
                <li data-id="element-2846">
                  <div className="flex items-center" data-id="element-2847">
                    <ChevronRight size={16} className="text-gray-400" data-id="element-2848" />
                    <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2" data-id="element-2849">
                      I Miei Eventi
                    </span>
                  </div>
                </li>
              </ol>
            </nav>

            <div className="flex justify-between items-center mb-8" data-id="element-2850">
              <h1 className="text-2xl font-semibold text-gray-800" data-id="element-2851">
                I Miei Eventi
              </h1>
              <button onClick={() => setIsCreateModalOpen(true)} className="flex items-center px-4 py-2 bg-custom-green text-white rounded-md hover:bg-custom-green/90 transition-colors" data-id="element-2852">
                <PlusIcon size={18} className="mr-2" data-id="element-2853" />
                Nuovo Evento
              </button>
            </div>

            {myHostedEvents.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2854">
                {myHostedEvents.map((event, index) => {
              // Check if the event is in the past
              const eventDate = new Date(event.date);
              const now = new Date();
              const isPastEvent = eventDate < now;
              // Simulate different participant states for demo purposes
              const modifiedEvent = {
                ...event,
                host: {
                  ...event.host,
                  avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4hFhikAPJ46vhrcKePNA5J/IMG_0014.jpg'
                },
                // Simulate different states based on index
                participants: index % 3 === 0 ? [] // No participants
                : index % 3 === 1 ? event.participants // With participants
                : [...event.participants],
                // Add pending requests ONLY for events that are not in the past
                pendingRequests: index % 3 === 2 && !isPastEvent ? [{
                  id: 'pending-1',
                  name: 'Marco Bianchi',
                  avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
                }, {
                  id: 'pending-2',
                  name: 'Giulia Verdi',
                  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'
                }] : []
              };
              return <EventCard key={event.id} event={modifiedEvent} onClick={() => handleCoffeeClick(event.id)} onEdit={handleEditCoffee} onDelete={handleDeleteCoffee} data-id="element-2855" />;
            })}
              </div> : <div className="bg-gray-50 rounded-lg p-6 text-center" data-id="element-2856">
                <p className="text-gray-600" data-id="element-2857">
                  Non hai ancora creato eventi. Crea il tuo primo evento
                  Greenback!
                </p>
              </div>}
          </div>
        </main>
      </div>

      <CreateCoffeeModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} data-id="element-2858" />
    </div>;
};
export default MyCoffeePage;